import { useEffect, useState } from "react";

import { ApiError, getRun } from "@/api/client";
import type { Run } from "@/api/client";

const ACTIVE: ReadonlySet<string> = new Set(["queued", "running"]);
const POLL_MS = 1500;

/** One run by id, polled until it reaches a state it will not leave. */
export function useRun(runId: string): { run: Run | null; error: string | null } {
  const [run, setRun] = useState<Run | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const next = await getRun(runId);
        if (cancelled) {
          return;
        }
        setRun(next);
        setError(null);
        if (ACTIVE.has(next.status)) {
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof ApiError ? err.detail : "could not reach the API");
        // A missing run will not appear by waiting; anything else might recover.
        if (!(err instanceof ApiError && err.status === 404)) {
          timer = setTimeout(() => void tick(), POLL_MS * 2);
        }
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [runId]);

  return { run, error };
}
